import type JQuery from "jquery";
import type Lenis from "lenis";
import SmoothScroll from "../singleton/SmoothScroll";
import WindowSize from "../singleton/WindowSize";
import { prefersReducedMotion, REDUCE_MOTION_QUERY } from "../utils/reducedMotion";

const MOBILE_BREAKPOINT = 768;
const FOCUSABLE_SELECTOR = 'a[href], button:not([disabled])';
const LOGO_SHUFFLE_INTERVAL_MS = 250;

class Header {
	private windowSize: WindowSize;
	$header: JQuery<HTMLElement>;
	$body: JQuery<HTMLElement>;
	$main: JQuery<HTMLElement>;
	$footer: JQuery<HTMLElement>;
	$hamburger: JQuery<HTMLElement>;
	$nav: JQuery<HTMLElement>;
	$parentMenuItems: JQuery<HTMLElement>;
	$subMenus: JQuery<HTMLElement>;
	$menuLinks: JQuery<HTMLElement>;
	$logoSvgs: JQuery<SVGElement>;

	headerIsOpened: boolean;
	isScrollingDisabled: boolean;
	isDark: boolean;
	isHome: boolean;
	threshold: number;
	smoothScroll: typeof SmoothScroll;

	private logoIndex = 0;
	private logoShuffleTimer: ReturnType<typeof setInterval> | null = null;
	private reduceMotionMq: MediaQueryList | null = null;

	private onDocumentKeyDown = (e: KeyboardEvent) =>
		this.handleDocumentKeyDown(e);

	private onReduceMotionChange = () => this.syncLogoShuffle();

	constructor() {
		this.$header = $(".header");
		this.$body = $("body");
		this.$main = $("#content");
		this.$footer = $("footer");
		this.$hamburger = this.$header.find(".header__hamburger");
		this.$nav = this.$header.find(".header__nav");
		this.$parentMenuItems = this.$nav.find(".menu-item-has-children > a");
		this.$subMenus = this.$nav.find(".sub-menu");
		this.$subMenus.attr("aria-expanded", "false");
		this.$menuLinks = this.$nav.find("a");
		this.$logoSvgs = this.$header.find(".header__logo > svg");

		this.headerIsOpened = false;
		this.isScrollingDisabled = false;
		this.isDark = false;
		this.isHome = this.$body.hasClass("home");
		this.threshold = 50;

		this.windowSize = WindowSize.getInstance();
		this.smoothScroll = SmoothScroll;

		this.init();
	}

	init() {
		this.$hamburger.on("click", this.onHamburgerClick.bind(this));
		this.smoothScroll.scroll.on("scroll", this.onScroll.bind(this));
		this.windowSize.addListener(this.onResize.bind(this));
		this.$parentMenuItems.on("click", this.dropdownButtonClick.bind(this));
		this.$nav.find("a").on("keydown", this.keyboardNavigation.bind(this));
		this.initLogoShuffle();
	}

	initLogoShuffle() {
		if (this.isHome || this.$logoSvgs.length < 2) {
			this.setActiveLogo(0);
			return;
		}

		this.setActiveLogo(0);

		this.reduceMotionMq = window.matchMedia(REDUCE_MOTION_QUERY);
		this.reduceMotionMq.addEventListener("change", this.onReduceMotionChange);
		this.syncLogoShuffle();
	}

	syncLogoShuffle() {
		if (prefersReducedMotion()) {
			this.stopLogoShuffle();
			this.setActiveLogo(0);
			return;
		}

		this.startLogoShuffle();
	}

	startLogoShuffle() {
		if (this.logoShuffleTimer !== null) return;

		this.logoShuffleTimer = setInterval(() => {
			const next = (this.logoIndex + 1) % this.$logoSvgs.length;
			this.setActiveLogo(next);
		}, LOGO_SHUFFLE_INTERVAL_MS);
	}

	stopLogoShuffle() {
		if (this.logoShuffleTimer === null) return;

		clearInterval(this.logoShuffleTimer);
		this.logoShuffleTimer = null;
	}

	setActiveLogo(index: number) {
		this.logoIndex = index;
		this.$logoSvgs.each((i, el) => {
			el.classList.toggle("is-active", i === index);
		});
	}

	isMobile() {
		return this.windowSize.getSize().width < MOBILE_BREAKPOINT;
	}

	onHamburgerClick() {
		if (!this.headerIsOpened) {
			this.openNav();
		} else {
			this.closeNav();
		}
	}

	getMobileFocusables(): HTMLElement[] {
		const header = this.$header.get(0);
		if (!header) return [];

		return Array.from(
			header.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
		).filter((el) => {
			if (el.hasAttribute("disabled") || el.getAttribute("aria-hidden") === "true") {
				return false;
			}
			// Skip inert / hidden ancestors
			return el.getClientRects().length > 0;
		});
	}

	handleDocumentKeyDown(e: KeyboardEvent) {
		if (!this.headerIsOpened || !this.isMobile()) return;

		if (e.key === "Escape") {
			e.preventDefault();

			const $openSubmenu = this.$subMenus.filter('[aria-expanded="true"]');
			if ($openSubmenu.length) {
				this.closeSubmenu($openSubmenu.first());
				$openSubmenu.first().parent().children("a").trigger("focus");
				return;
			}

			this.closeNav({ restoreFocus: true });
			return;
		}

		if (e.key !== "Tab") return;

		const focusables = this.getMobileFocusables();
		if (!focusables.length) {
			e.preventDefault();
			return;
		}

		const first = focusables[0];
		const last = focusables[focusables.length - 1];
		const active = document.activeElement as HTMLElement | null;

		if (e.shiftKey) {
			if (active === first || !headerContains(this.$header.get(0), active)) {
				e.preventDefault();
				last.focus();
			}
		} else if (active === last || !headerContains(this.$header.get(0), active)) {
			e.preventDefault();
			first.focus();
		}
	}

	keyboardNavigation(e: JQuery.KeyDownEvent) {
		const { key } = e;

		// Key handling for improved keyboard navigation
		if (
			![
				"Enter",
				"ArrowUp",
				"ArrowRight",
				"ArrowLeft",
				"ArrowDown",
				"Tab",
				"Escape",
				" ",
			].includes(key)
		) {
			return;
		}

		const $currentTarget = $(e.currentTarget); // link
		const $parentLi = $currentTarget.parent();
		const $parentList = $parentLi.parent("ul"); // li
		const isSubMenu = $parentList.hasClass("sub-menu"); // check if it's a submenu
		const hasSubMenu = $parentLi.find(".sub-menu").length > 0; // check if it has a submenu

		switch (key) {
			// Closes any open submenu and returns focus to its parent.
			// On mobile drawer: Escape closes the whole menu when not in a submenu.
			case "Escape": {
				e.preventDefault();
				e.stopPropagation();

				if (isSubMenu) {
					const $parentItem = $parentList.parent();
					this.closeSubmenu($parentList);
					$parentItem.children("a").trigger("focus");
				} else if (this.headerIsOpened && this.isMobile()) {
					this.closeNav({ restoreFocus: true });
				}
				break;
			}

			// On menubar item: moves focus to the previous top-level item.
			// In a submenu, closes the submenu and returns focus to its parent item.
			case "ArrowLeft": {
				e.preventDefault();

				if (isSubMenu) {
					const $parentItem = $parentList.parent();
					this.closeSubmenu($parentList);
					$parentItem.children("a").trigger("focus");
				} else {
					const $prevItem = $parentLi.prev("li").children("a");
					if ($prevItem.length) {
						$prevItem.trigger("focus");
					} else {
						// If no previous item, focus the last item in the menu
						const $lastItem = $parentList.children("li").last().children("a");
						$lastItem.trigger("focus");
					}
				}

				break;
			}

			// On menubar item: moves focus to the next top-level item.
			// If the item has a submenu, optionally open it.
			case "ArrowRight": {
				e.preventDefault();

				if (isSubMenu) {
					const $parentItem = $parentList.parent();
					this.closeSubmenu($parentList);
					$parentItem.children("a").trigger("focus");
				} else {
					const $nextItem = $parentLi.next("li").children("a");
					if ($nextItem.length) {
						$nextItem.trigger("focus");
					} else {
						// If no next item, focus the first item in the menu
						const $firstItem = $parentList.children("li").first().children("a");
						$firstItem.trigger("focus");
					}
				}
				break;
			}

			// On menubar item: opens submenu (if exists) and focuses first item.
			// On submenu item: moves focus to the next item in the submenu.
			case "ArrowDown": {
				e.preventDefault();

				if (isSubMenu) {
					const $nextItem = $parentLi.next("li").children("a");
					if ($nextItem.length) {
						$nextItem.trigger("focus");
					} else {
						// If no next item, focus the first item in the menu
						const $firstItem = $parentList.children("li").first().children("a");
						$firstItem.trigger("focus");
					}
				} else if (hasSubMenu) {
					const $submenu = $parentLi.children(".sub-menu");
					if ($submenu.length) {
						this.openSubmenu($submenu);
						$submenu.find("a").first().trigger("focus");
					} else {
						const $nextItem = $parentLi.next("li").children("a");
						if ($nextItem.length) {
							$nextItem.trigger("focus");
						} else {
							// If no next item, focus the first item in the menu
							const $firstItem = $parentList
								.children("li")
								.first()
								.children("a");
							$firstItem.trigger("focus");
						}
					}
				}
				break;
			}

			// On submenu item: moves focus to the previous item.
			// If on the first submenu item, wraps to the last one.
			case "ArrowUp": {
				e.preventDefault();

				if (isSubMenu) {
					const $prevItem = $parentLi.prev("li").children("a");
					if ($prevItem.length) {
						$prevItem.trigger("focus");
					} else {
						// If no previous item, focus the last item in the menu
						const $lastItem = $parentList.children("li").last().children("a");
						$lastItem.trigger("focus");
					}
				} else if (hasSubMenu) {
					const $submenu = $parentLi.children(".sub-menu");
					if ($submenu.length) {
						this.openSubmenu($submenu);
						$submenu.find("a").last().trigger("focus");
					}
				} else {
					const $prevItem = $parentLi.prev("li").children("a");
					if ($prevItem.length) {
						$prevItem.trigger("focus");
					} else {
						// If no previous item, focus the last item in the menu
						const $lastItem = $parentList.children("li").last().children("a");
						$lastItem.trigger("focus");
					}
				}
				break;
			}

			case "Enter":
			case " ": {
				e.preventDefault();

				if (hasSubMenu) {
					if ($parentLi.hasClass("is-opened")) {
						// if submenu is opened, navigate to the link
						const $link = $parentLi.children("a");
						if ($link.length) {
							$link.get(0)?.click();
						}
					} else {
						const $submenu = $parentLi.children(".sub-menu");
						if ($submenu.length) {
							this.openSubmenu($submenu);
							$submenu.find("a").first().trigger("focus");
						}
					}
				} else {
					// If it's a link, allow default behavior
					const $link = $parentLi.children("a");

					if ($link.length) {
						$link.get(0)?.click();
					}
				}
				break;
			}

			case "Tab": {
				// if tabbing out of submenu, close it
				if (isSubMenu) {
					const $submenu = $parentLi.closest(".sub-menu");
					const isLastItem = $parentLi.is(":last-child");

					if (isLastItem) {
						this.closeSubmenu($submenu);
						return;
					}

					// check if there is other opened submenu
					const $otherSubmenus = this.$subMenus.not($submenu);
					if ($otherSubmenus.length) {
						$otherSubmenus.each((_index, submenu) => {
							const $submenu = $(submenu);
							this.closeSubmenu($submenu);
						});
					}
				}
				break;
			}

			default:
				// Ignore any other keys
				break;
		}
	}

	dropdownButtonClick(e: JQuery.ClickEvent) {
		e.preventDefault();

		const $target = $(e.currentTarget);
		const $parent = $target.closest("li");
		const $submenu = $parent.find(".sub-menu");

		if ($parent.hasClass("is-opened")) {
			this.closeSubmenu($submenu);
		} else {
			this.openSubmenu($submenu);
		}
	}

	openSubmenu($submenu: JQuery<HTMLElement>) {
		const $parent = $submenu.parent();

		if (this.isMobile()) {
			this.$subMenus.slideUp();
			$submenu.slideDown();
		}

		this.$subMenus.attr("aria-expanded", "false");
		this.$parentMenuItems.parent().removeClass("is-opened");
		$parent.addClass("is-opened");
		$submenu.attr("aria-expanded", "true");
	}

	closeSubmenu($submenu: JQuery<HTMLElement>) {
		const $parent = $submenu.parent();
		$parent.removeClass("is-opened");

		if (this.isMobile()) {
			$submenu.slideUp();
		}

		$submenu.attr("aria-expanded", "false");
	}

	setBackgroundInert(inert: boolean) {
		if (inert) {
			this.$main.attr("inert", "");
			this.$footer.attr("inert", "");
		} else {
			this.$main.removeAttr("inert");
			this.$footer.removeAttr("inert");
		}
	}

	openNav() {
		this.smoothScroll.disableScrolling();
		this.$nav.attr("data-open", "true");
		this.$hamburger.attr("aria-expanded", "true");
		this.$header.addClass("is-opened");

		if (this.isMobile()) {
			this.setBackgroundInert(true);
			document.addEventListener("keydown", this.onDocumentKeyDown);

			requestAnimationFrame(() => {
				const firstLink = this.$menuLinks.get(0);
				firstLink?.focus();
			});
		}

		this.headerIsOpened = true;
		this.isScrollingDisabled = true;
	}

	closeNav(options: { restoreFocus?: boolean } = {}) {
		const { restoreFocus = true } = options;

		this.$nav.attr("data-open", "false");
		this.$hamburger.attr("aria-expanded", "false");
		this.$header.removeClass("is-opened");
		this.setBackgroundInert(false);
		document.removeEventListener("keydown", this.onDocumentKeyDown);

		if (this.isScrollingDisabled) {
			this.smoothScroll.enableScrolling();
			this.isScrollingDisabled = false;
		}

		this.headerIsOpened = false;

		if (restoreFocus) {
			this.$hamburger.trigger("focus");
		}
	}

	onResize() {
		if (this.headerIsOpened) {
			// Closing because of breakpoint change — don't yank focus to hamburger
			this.closeNav({ restoreFocus: false });
		}

		const { width } = this.windowSize.getSize();
		if (width < MOBILE_BREAKPOINT) {
			this.$subMenus.slideUp();
		} else {
			this.$subMenus.show();
			// Desktop: nav is always shown via CSS; keep data-open false
			this.$nav.attr("data-open", "false");
		}
	}

	onScroll(e: Lenis) {
		const st = e.scroll;

		if (e.isStopped) return;

		if (e.direction === -1) {
			this.$header.addClass("is-scrolling-up");
		} else {
			this.$header.removeClass("is-scrolling-up");
		}

		if (st > this.threshold) {
			this.$header.addClass("is-scrolling");
		} else {
			this.$header.removeClass("is-scrolling");
		}
	}
}

function headerContains(
	header: HTMLElement | undefined,
	node: Node | null,
): boolean {
	return Boolean(header && node && header.contains(node));
}

export default Header;
